// client/src/pages/MyProfile.tsx - FIXED PROFILE PICTURE UPLOAD + DOCUMENT MANAGEMENT + FIXED DELETE ACCOUNT
import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { useNavigate } from 'react-router-dom';
import SubscriptionModal from '../components/SubscriptionModal';

export default function MyProfile() {
    const [profile, setProfile] = useState<any>(null);
    const [userEmail, setUserEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [editMode, setEditMode] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [newPhone, setNewPhone] = useState('');
    const [debugInfo, setDebugInfo] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [profilePictureFile, setProfilePictureFile] = useState<File | null>(null);
    const [uploadingPicture, setUploadingPicture] = useState(false);
    const [subscription, setSubscription] = useState<any>(null);
    const [loadingSubscription, setLoadingSubscription] = useState(true);
    const [cancellingSubscription, setCancellingSubscription] = useState(false);
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

    // Document upload states
    const [cvFile, setCvFile] = useState<File | null>(null);
    const [feeFile, setFeeFile] = useState<File | null>(null);
    const [additionalDocsFiles, setAdditionalDocsFiles] = useState<File[]>([]);
    const [uploadingDocs, setUploadingDocs] = useState(false);

    const navigate = useNavigate();

    const addDebug = (msg: string) => {
        // DEBUG INFO - UNCOMMENT TO ENABLE
        // console.log('🔵 PROFILE DEBUG:', msg);
        // setDebugInfo(prev => [...prev.slice(-9), `${new Date().toLocaleTimeString()}: ${msg}`]);
    };

    useEffect(() => {
        addDebug('MyProfile component mounted');
        loadProfile();
        loadSubscription();
    }, []);

    // Add subscription check function
    const loadSubscription = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            const { data } = await supabase
                .from('subscriptions')
                .select('*')
                .eq('user_id', user.id)
                .eq('status', 'active')
                .single();

            setSubscription(data);
        } catch (error) {
            console.error('Error loading subscription:', error);
        } finally {
            setLoadingSubscription(false);
        }
    };

    // Add cancel subscription function
    const cancelSubscription = async () => {
        if (!subscription || !window.confirm('Are you sure you want to cancel your subscription?')) {
            return;
        }

        setCancellingSubscription(true);
        try {
            // In test mode, just update database
            if (subscription.stripe_subscription_id?.startsWith('test_')) {
                await supabase
                    .from('subscriptions')
                    .update({ status: 'cancelled' })
                    .eq('id', subscription.id);

                setSubscription(null);
                setMessage('Test subscription cancelled successfully.');
            } else {
                // Real cancellation through Stripe
                const response = await fetch(`${process.env.REACT_APP_API_URL}/cancel-subscription`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        subscription_id: subscription.stripe_subscription_id
                    })
                });

                if (!response.ok) throw new Error('Failed to cancel subscription');

                setSubscription(null);
                setMessage('Subscription cancelled successfully.');
            }
        } catch (error: any) {
            setMessage('Error cancelling subscription: ' + error.message);
        } finally {
            setCancellingSubscription(false);
        }
    };

    const loadProfile = async () => {
        try {
            setIsLoading(true);
            addDebug('Starting profile load...');

            const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
            addDebug(`Session check: ${sessionData?.session ? 'EXISTS' : 'NULL'} (Error: ${sessionError?.message || 'none'})`);

            const { data: { user }, error: userError } = await supabase.auth.getUser();
            addDebug(`User fetch: ${user ? user.email : 'NULL'} (Error: ${userError?.message || 'none'})`);

            if (userError) {
                addDebug(`User error: ${userError.message}`);
                setMessage(`Error getting user: ${userError.message}`);
                return;
            }

            if (!user) {
                addDebug('No user found - should redirect to login');
                setMessage('No user found - please log in');
                return;
            }

            setUserEmail(user.email || '');
            addDebug(`User found: ${user.id} (${user.email})`);

            // Try experts table first
            addDebug('Checking experts table...');
            const { data: expert, error: expertError } = await supabase
                .from('experts')
                .select('*')
                .eq('user_id', user.id)
                .single();

            addDebug(`Expert query result: ${expert ? 'FOUND' : 'NULL'} (Error: ${expertError?.message || 'none'})`);

            if (expert) {
                setProfile({ ...expert, type: 'expert' });
                setNewEmail(expert.email);
                setNewPhone(expert.phone_number);
                addDebug(`Expert profile loaded: ${expert.full_name}`);
            } else {
                // Try attorneys table
                addDebug('Checking attorneys table...');
                const { data: attorney, error: attorneyError } = await supabase
                    .from('attorneys')
                    .select('*')
                    .eq('user_id', user.id)
                    .single();

                addDebug(`Attorney query result: ${attorney ? 'FOUND' : 'NULL'} (Error: ${attorneyError?.message || 'none'})`);

                if (attorney) {
                    setProfile({ ...attorney, type: 'attorney' });
                    setNewEmail(attorney.email);
                    setNewPhone(attorney.phone_number);
                    addDebug(`Attorney profile loaded: ${attorney.name}`);
                } else {
                    addDebug('No profile found in either table');
                    setMessage('No profile found');
                }
            }
        } catch (error: any) {
            console.error('🔴 Profile load error:', error);
            addDebug(`Profile load error: ${error.message}`);
            setMessage(`Error loading profile: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePasswordReset = async () => {
        setLoading(true);
        setMessage('');
        addDebug('Sending password reset email...');

        const { error } = await supabase.auth.resetPasswordForEmail(userEmail, {
            redirectTo: `${window.location.origin}/reset-password`,
        });

        setLoading(false);

        if (error) {
            addDebug(`Password reset error: ${error.message}`);
            setMessage('Error: ' + error.message);
        } else {
            addDebug('Password reset email sent successfully');
            setMessage('Password reset email sent! Check your inbox.');
        }
    };

    const handleProfilePictureUpload = async () => {
        if (!profilePictureFile || !profile) return;

        setUploadingPicture(true);
        setMessage('');
        addDebug('Uploading profile picture...');

        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Not authenticated');

            // Upload new profile picture with correct folder structure
            const fileExt = profilePictureFile.name.split('.').pop();
            const fileName = `${user.id}-${Date.now()}.${fileExt}`;
            const filePath = `profiles/${fileName}`; // Use profiles folder

            addDebug(`Uploading to: ${filePath}`);

            const { data: uploadData, error: uploadError } = await supabase.storage
                .from('profile-pictures')
                .upload(filePath, profilePictureFile, {
                    cacheControl: '3600',
                    upsert: true
                });

            if (uploadError) {
                addDebug(`Upload error: ${uploadError.message}`);
                throw uploadError;
            }

            addDebug(`Upload successful: ${uploadData.path}`);

            // Get public URL
            const { data: urlData } = supabase.storage
                .from('profile-pictures')
                .getPublicUrl(filePath);

            const newPictureUrl = urlData.publicUrl;
            addDebug(`New picture URL: ${newPictureUrl}`);

            // Update profile in database
            const table = profile.type === 'expert' ? 'experts' : 'attorneys';
            const { error: updateError } = await supabase
                .from(table)
                .update({
                    profile_picture_url: newPictureUrl,
                })
                .eq('id', profile.id);

            if (updateError) {
                addDebug(`Database update error: ${updateError.message}`);
                throw updateError;
            }

            // Update local state
            setProfile((prev: any) => ({ ...prev, profile_picture_url: newPictureUrl }));
            setProfilePictureFile(null);
            setMessage('Profile picture updated successfully!');
            addDebug('Profile picture process completed successfully');

            // Clear the file input
            const fileInput = document.getElementById('profile-picture-upload') as HTMLInputElement;
            if (fileInput) fileInput.value = '';

        } catch (error: any) {
            console.error('Profile picture upload error:', error);
            addDebug(`Profile picture upload error: ${error.message}`);
            setMessage('Error updating profile picture: ' + error.message);
        } finally {
            setUploadingPicture(false);
        }
    };

    const uploadDocument = async (file: File, bucket: string, fileType: 'cv' | 'fee') => {
        try {
            setUploadingDocs(true);
            addDebug(`Starting ${fileType} upload...`);

            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Not authenticated');

            const timestamp = Date.now();
            const fileExt = file.name.split('.').pop();
            const fileName = `${user.id}-${timestamp}.${fileExt}`;

            // Try to upload to the bucket
            const { data: uploadData, error: uploadError } = await supabase.storage
                .from(bucket)
                .upload(fileName, file, {
                    cacheControl: '3600',
                    upsert: true
                });

            if (uploadError) {
                // If bucket doesn't exist, try to create it
                if (uploadError.message.includes('not found')) {
                    addDebug(`Creating bucket ${bucket}...`);
                    const { error: createError } = await supabase.storage.createBucket(bucket, {
                        public: true
                    });

                    if (createError) {
                        addDebug(`Bucket creation failed: ${createError.message}`);
                    }

                    // Retry upload
                    const { data: retryData, error: retryError } = await supabase.storage
                        .from(bucket)
                        .upload(fileName, file, {
                            cacheControl: '3600',
                            upsert: true
                        });

                    if (retryError) throw retryError;
                    addDebug(`Retry upload successful`);
                } else {
                    throw uploadError;
                }
            }

            // Get public URL
            const { data: urlData } = supabase.storage
                .from(bucket)
                .getPublicUrl(fileName);

            const newDocUrl = urlData.publicUrl;
            addDebug(`Upload successful: ${newDocUrl}`);

            // Update profile in database
            const updateField = fileType === 'cv' ? 'cv_url' : 'fee_schedule_url';
            const { error: updateError } = await supabase
                .from('experts')
                .update({ [updateField]: newDocUrl })
                .eq('id', profile.id);

            if (updateError) throw updateError;

            // Update local state
            setProfile((prev: any) => ({ ...prev, [updateField]: newDocUrl }));

            // Clear file inputs
            if (fileType === 'cv') {
                setCvFile(null);
                const cvInput = document.getElementById('cv-upload') as HTMLInputElement;
                if (cvInput) cvInput.value = '';
            } else {
                setFeeFile(null);
                const feeInput = document.getElementById('fee-upload') as HTMLInputElement;
                if (feeInput) feeInput.value = '';
            }

            setMessage(`${fileType === 'cv' ? 'CV' : 'Fee schedule'} uploaded successfully!`);
            addDebug(`${fileType} upload process completed`);

        } catch (error: any) {
            console.error(`${fileType} upload error:`, error);
            addDebug(`${fileType} upload error: ${error.message}`);
            setMessage(`Error uploading ${fileType === 'cv' ? 'CV' : 'fee schedule'}: ${error.message}`);
        } finally {
            setUploadingDocs(false);
        }
    };

    const handleUpdateProfile = async () => {
        setLoading(true);
        setMessage('');
        addDebug('Updating profile...');

        const table = profile.type === 'expert' ? 'experts' : 'attorneys';
        const { error } = await supabase
            .from(table)
            .update({
                email: newEmail,
                phone_number: newPhone,
            })
            .eq('id', profile.id);

        setLoading(false);

        if (error) {
            addDebug(`Update error: ${error.message}`);
            setMessage('Error updating profile: ' + error.message);
        } else {
            addDebug('Profile updated successfully');
            setMessage('Profile updated successfully!');
            setEditMode(false);
            loadProfile();
        }
    };

    const handleDeleteAccount = async () => {
        const deleteText = 'DELETE';
        const userInput = window.prompt(
            `Are you sure you want to delete your account? This action cannot be undone.\n\nType "${deleteText}" to confirm:`
        );

        if (userInput !== deleteText) {
            setMessage('Account deletion cancelled.');
            return;
        }

        const emailConfirmed = window.confirm(
            `Final confirmation: We will send a deletion confirmation email to ${userEmail}. Do you want to proceed?`
        );

        if (!emailConfirmed) {
            setMessage('Account deletion cancelled.');
            return;
        }

        setLoading(true);
        addDebug('Deleting account...');
        setMessage('Processing account deletion...');

        try {
            // FIXED: Delete related data first to avoid foreign key constraint violations

            // Step 1: Delete all messages where this user is involved
            if (profile.type === 'expert') {
                // Delete messages where this expert is involved
                const { error: messagesError } = await supabase
                    .from('messages')
                    .delete()
                    .eq('expert_id', profile.id);

                if (messagesError) {
                    console.error('Error deleting expert messages:', messagesError);
                    // Continue anyway as some messages might not exist
                }

                // Delete contacts where this expert is involved
                const { error: contactsError } = await supabase
                    .from('contacts')
                    .delete()
                    .eq('expert_id', profile.id);

                if (contactsError) {
                    console.error('Error deleting expert contacts:', contactsError);
                    // Continue anyway
                }
            } else {
                // Delete messages where this attorney is involved
                const { error: messagesError } = await supabase
                    .from('messages')
                    .delete()
                    .eq('attorney_id', profile.id);

                if (messagesError) {
                    console.error('Error deleting attorney messages:', messagesError);
                    // Continue anyway
                }

                // Delete contacts where this attorney is involved
                const { error: contactsError } = await supabase
                    .from('contacts')
                    .delete()
                    .eq('attorney_id', profile.id);

                if (contactsError) {
                    console.error('Error deleting attorney contacts:', contactsError);
                    // Continue anyway
                }

                // Delete subscriptions if attorney
                const { error: subError } = await supabase
                    .from('subscriptions')
                    .delete()
                    .eq('user_id', profile.user_id);

                if (subError) {
                    console.error('Error deleting subscriptions:', subError);
                    // Continue anyway
                }
            }

            // Step 2: Now delete the profile
            const table = profile.type === 'expert' ? 'experts' : 'attorneys';
            const { error: profileError } = await supabase
                .from(table)
                .delete()
                .eq('id', profile.id);

            if (profileError) {
                throw profileError;
            }

            // Step 3: Sign out user
            await supabase.auth.signOut();

            addDebug('Account deleted successfully');
            setMessage('Account successfully deleted. You have been logged out.');

            setTimeout(() => {
                navigate('/');
            }, 2000);

        } catch (error: any) {
            console.error('Delete account error:', error);
            addDebug(`Delete error: ${error.message}`);
            setMessage('Error deleting account: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center max-w-md">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading your profile...</p>

                    {/* DEBUG INFO - UNCOMMENT TO ENABLE */}
                    {/* <div className="mt-6 p-4 bg-blue-50 rounded-lg text-left">
                        <h4 className="font-medium text-blue-900 mb-2">Debug Info:</h4>
                        <div className="text-sm text-blue-700 space-y-1 max-h-48 overflow-y-auto">
                            {debugInfo.map((info, idx) => (
                                <div key={idx}>{info}</div>
                            ))}
                        </div>
                    </div> */}
                </div>
            </div>
        );
    }

    if (!profile) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center max-w-md">
                    <p className="text-gray-600 mb-4">No profile found</p>

                    {/* DEBUG INFO - UNCOMMENT TO ENABLE */}
                    {/* <div className="p-4 bg-red-50 rounded-lg text-left">
                        <h4 className="font-medium text-red-900 mb-2">Debug Info:</h4>
                        <div className="text-sm text-red-700 space-y-1 max-h-48 overflow-y-auto">
                            {debugInfo.map((info, idx) => (
                                <div key={idx}>{info}</div>
                            ))}
                        </div>
                        <button
                            onClick={loadProfile}
                            className="mt-3 px-3 py-1 bg-blue-600 text-white rounded text-sm"
                        >
                            Retry Load
                        </button>
                    </div> */}

                    {message && (
                        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded">
                            {message}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="bg-white rounded-lg shadow-xl overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-4">
                        <h2 className="text-2xl font-bold text-white">My Profile</h2>
                    </div>

                    <div className="p-6 space-y-6">
                        {/* DEBUG PANEL - UNCOMMENT TO ENABLE */}
                        {/* <details className="bg-blue-50 p-3 rounded">
                            <summary className="cursor-pointer text-blue-800 font-medium">Debug Information</summary>
                            <div className="mt-2 text-sm text-blue-700 space-y-1 max-h-32 overflow-y-auto">
                                {debugInfo.map((info, idx) => (
                                    <div key={idx}>{info}</div>
                                ))}
                            </div>
                        </details> */}

                        {/* Enhanced Profile Picture Section */}
                        <div className="flex items-center space-x-6">
                            <div className="relative">
                                <img
                                    src={profile.profile_picture_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.full_name || profile.name)}&background=0D8ABC&color=fff&size=200`}
                                    alt="Profile"
                                    className="w-24 h-24 rounded-full object-cover border-4 border-gray-200"
                                />
                                <label
                                    htmlFor="profile-picture-upload"
                                    className="absolute inset-0 w-24 h-24 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                                >
                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                </label>
                                <input
                                    id="profile-picture-upload"
                                    type="file"
                                    accept="image/jpeg,image/png,image/gif,image/webp"
                                    className="hidden"
                                    onChange={(e) => setProfilePictureFile(e.target.files?.[0] || null)}
                                />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-2xl font-semibold text-gray-900">
                                    {profile.full_name || profile.name}
                                </h3>
                                <p className="text-gray-600 capitalize">{profile.type}</p>
                                {profile.type === 'expert' && profile.specialty && (
                                    <p className="text-gray-600">{profile.specialty}</p>
                                )}
                                <div className="flex items-center space-x-4 mt-2">
                                    <p className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                        profile.is_verified
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                        {profile.is_verified ? '✓ Verified' : 'Pending Verification'}
                                    </p>
                                    {profilePictureFile && (
                                        <button
                                            onClick={handleProfilePictureUpload}
                                            disabled={uploadingPicture}
                                            className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:bg-gray-400"
                                        >
                                            {uploadingPicture ? 'Uploading...' : 'Update Picture'}
                                        </button>
                                    )}
                                </div>
                                {profilePictureFile && (
                                    <p className="text-sm text-gray-600 mt-1">
                                        Selected: {profilePictureFile.name}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Contact Information */}
                        <div className="border-t pt-8">
                            <h4 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h4>
                            {editMode ? (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Email
                                        </label>
                                        <input
                                            type="email"
                                            value={newEmail}
                                            onChange={e => setNewEmail(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Note: Changing email will require verification
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Phone
                                        </label>
                                        <input
                                            type="tel"
                                            value={newPhone}
                                            onChange={e => setNewPhone(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div className="flex space-x-3">
                                        <button
                                            onClick={handleUpdateProfile}
                                            disabled={loading}
                                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                                        >
                                            {loading ? 'Saving...' : 'Save Changes'}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setEditMode(false);
                                                setNewEmail(profile.email);
                                                setNewPhone(profile.phone_number);
                                            }}
                                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <div>
                                        <p className="text-sm text-gray-600">Email</p>
                                        <p className="font-medium">{profile.email}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600">Phone</p>
                                        <p className="font-medium">{profile.phone_number}</p>
                                    </div>
                                    <button
                                        onClick={() => setEditMode(true)}
                                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                                    >
                                        Edit Contact Info
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Professional Information */}
                        {profile.type === 'expert' && (
                            <div className="border-t pt-8">
                                <h4 className="text-lg font-semibold text-gray-900 mb-4">Professional Information</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <p className="text-sm text-gray-600">Expert Type</p>
                                        <p className="font-medium capitalize">{profile.expert_type || 'Physician'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600">Specialty</p>
                                        <p className="font-medium">{profile.specialty}</p>
                                    </div>
                                    {profile.sub_specialty && (
                                        <div>
                                            <p className="text-sm text-gray-600">Sub-specialty</p>
                                            <p className="font-medium">{profile.sub_specialty}</p>
                                        </div>
                                    )}
                                    <div>
                                        <p className="text-sm text-gray-600">Years Practicing</p>
                                        <p className="font-medium">{profile.years_practicing}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600">NPI/License</p>
                                        <p className="font-medium">{profile.npi || 'Not provided'}</p>
                                    </div>
                                    {profile.expert_type === 'nurse' && (
                                        <>
                                            <div>
                                                <p className="text-sm text-gray-600">Nursing Degree</p>
                                                <p className="font-medium">{profile.nursing_degree || 'Not specified'}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-600">Years as RN</p>
                                                <p className="font-medium">{profile.years_as_nurse || 'Not specified'}</p>
                                            </div>
                                            {profile.nursing_certifications?.length > 0 && (
                                                <div className="md:col-span-2">
                                                    <p className="text-sm text-gray-600">Nursing Certifications</p>
                                                    <p className="font-medium">{profile.nursing_certifications.join(', ')}</p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {profile.states_licensed?.length > 0 && (
                                        <div className="md:col-span-2">
                                            <p className="text-sm text-gray-600">States Licensed</p>
                                            <p className="font-medium">{profile.states_licensed.join(', ')}</p>
                                        </div>
                                    )}
                                    {profile.certifications?.length > 0 && (
                                        <div className="md:col-span-2">
                                            <p className="text-sm text-gray-600">Certifications</p>
                                            <p className="font-medium">{profile.certifications.join(', ')}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {profile.type === 'attorney' && (
                            <div className="border-t pt-8">
                                <h4 className="text-lg font-semibold text-gray-900 mb-4">Professional Information</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <p className="text-sm text-gray-600">Firm</p>
                                        <p className="font-medium">{profile.firm_name}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600">License Number</p>
                                        <p className="font-medium">{profile.license_number || 'Not provided'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600">State of Practice</p>
                                        <p className="font-medium">{profile.state_of_practice}</p>
                                    </div>
                                    {profile.years_experience && (
                                        <div>
                                            <p className="text-sm text-gray-600">Years of Experience</p>
                                            <p className="font-medium">{profile.years_experience}</p>
                                        </div>
                                    )}
                                    {profile.firm_size && (
                                        <div>
                                            <p className="text-sm text-gray-600">Firm Size</p>
                                            <p className="font-medium">{profile.firm_size}</p>
                                        </div>
                                    )}
                                    {profile.practice_areas?.length > 0 && (
                                        <div className="md:col-span-2">
                                            <p className="text-sm text-gray-600">Practice Areas</p>
                                            <p className="font-medium">{profile.practice_areas.join(', ')}</p>
                                        </div>
                                    )}
                                    {profile.cases_handled?.length > 0 && (
                                        <div className="md:col-span-2">
                                            <p className="text-sm text-gray-600">Case Types Handled</p>
                                            <p className="font-medium">{profile.cases_handled.join(', ')}</p>
                                        </div>
                                    )}
                                    {profile.search_keywords && (
                                        <div className="md:col-span-2">
                                            <p className="text-sm text-gray-600">Search Keywords</p>
                                            <p className="font-medium">{profile.search_keywords}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Document Management for Experts */}
                        {profile.type === 'expert' && (
                            <div className="border-t pt-8">
                                <h4 className="text-lg font-semibold text-gray-900 mb-4">Documents</h4>

                                {/* Current Documents */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                    <div className="border rounded-lg p-4">
                                        <h5 className="font-medium text-gray-900 mb-2">Curriculum Vitae (CV)</h5>
                                        {profile.cv_url ? (
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-green-600">✓ CV Uploaded</span>
                                                <div className="flex space-x-2">
                                                    <a
                                                        href={profile.cv_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-blue-600 hover:text-blue-700 text-sm"
                                                    >
                                                        View
                                                    </a>
                                                    <button
                                                        onClick={() => {
                                                            const link = document.createElement('a');
                                                            link.href = profile.cv_url;
                                                            link.download = `${profile.full_name}-CV.pdf`;
                                                            link.click();
                                                        }}
                                                        className="text-green-600 hover:text-green-700 text-sm"
                                                    >
                                                        Download
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="text-sm text-gray-500">No CV uploaded</span>
                                        )}
                                    </div>

                                    <div className="border rounded-lg p-4">
                                        <h5 className="font-medium text-gray-900 mb-2">Fee Schedule</h5>
                                        {profile.fee_schedule_url ? (
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-green-600">✓ Fee Schedule Uploaded</span>
                                                <div className="flex space-x-2">
                                                    <a
                                                        href={profile.fee_schedule_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-blue-600 hover:text-blue-700 text-sm"
                                                    >
                                                        View
                                                    </a>
                                                    <button
                                                        onClick={() => {
                                                            const link = document.createElement('a');
                                                            link.href = profile.fee_schedule_url;
                                                            link.download = `${profile.full_name}-FeeSchedule.pdf`;
                                                            link.click();
                                                        }}
                                                        className="text-green-600 hover:text-green-700 text-sm"
                                                    >
                                                        Download
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="text-sm text-gray-500">No fee schedule uploaded</span>
                                        )}
                                    </div>
                                </div>

                                {/* Upload New Documents */}
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <h5 className="font-medium text-gray-900 mb-4">Upload/Update Documents</h5>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* CV Upload */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {profile.cv_url ? 'Update CV' : 'Upload CV'}
                                            </label>
                                            <div className="space-y-2">
                                                <input
                                                    id="cv-upload"
                                                    type="file"
                                                    accept=".pdf,.doc,.docx"
                                                    onChange={(e) => setCvFile(e.target.files?.[0] || null)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent file:mr-4 file:py-1 file:px-2 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                                />
                                                {cvFile && (
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-sm text-gray-600">Selected: {cvFile.name}</span>
                                                        <button
                                                            onClick={() => uploadDocument(cvFile, 'cv-uploads', 'cv')}
                                                            disabled={uploadingDocs}
                                                            className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:bg-gray-400"
                                                        >
                                                            {uploadingDocs ? 'Uploading...' : 'Upload'}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">PDF, DOC, or DOCX format (max 10MB)</p>
                                        </div>

                                        {/* Fee Schedule Upload */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {profile.fee_schedule_url ? 'Update Fee Schedule' : 'Upload Fee Schedule'}
                                            </label>
                                            <div className="space-y-2">
                                                <input
                                                    id="fee-upload"
                                                    type="file"
                                                    accept=".pdf"
                                                    onChange={(e) => setFeeFile(e.target.files?.[0] || null)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent file:mr-4 file:py-1 file:px-2 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                                />
                                                {feeFile && (
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-sm text-gray-600">Selected: {feeFile.name}</span>
                                                        <button
                                                            onClick={() => uploadDocument(feeFile, 'fee-schedules', 'fee')}
                                                            disabled={uploadingDocs}
                                                            className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:bg-gray-400"
                                                        >
                                                            {uploadingDocs ? 'Uploading...' : 'Upload'}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">PDF format only (max 5MB)</p>
                                        </div>

                                        {/* Additional Documents Upload */}
                                        <div className="col-span-2 border-t pt-4 mt-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Additional Documents
                                            </label>

                                            {/* Show existing additional docs */}
                                            {profile.additional_docs_url && (
                                                <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                                    <p className="text-sm font-medium text-green-800 mb-2">Current Documents:</p>
                                                    {(() => {
                                                        let urls: string[] = [];
                                                        try {
                                                            const parsed = JSON.parse(profile.additional_docs_url);
                                                            urls = Array.isArray(parsed) ? parsed : [profile.additional_docs_url];
                                                        } catch {
                                                            urls = [profile.additional_docs_url];
                                                        }
                                                        return (
                                                            <div className="space-y-1">
                                                                {urls.map((url: string, idx: number) => (
                                                                    <div key={idx} className="flex items-center justify-between">
                                                                        <span className="text-sm text-green-700">Document {idx + 1}</span>
                                                                        <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">View</a>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            )}

                                            {/* File list with remove buttons */}
                                            {additionalDocsFiles.length > 0 && (
                                                <div className="mb-3 space-y-2">
                                                    {additionalDocsFiles.map((file, idx) => (
                                                        <div key={idx} className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded-lg">
                                                            <span className="text-sm text-blue-700 flex items-center">
                                                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                </svg>
                                                                {file.name}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setAdditionalDocsFiles(additionalDocsFiles.filter((_, i) => i !== idx));
                                                                }}
                                                                className="text-red-500 hover:text-red-700 p-1"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Add file button */}
                                            {additionalDocsFiles.length < 10 && (
                                                <div className="mb-3">
                                                    <label className="cursor-pointer">
                                                        <div className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors">
                                                            <svg className="w-5 h-5 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                            </svg>
                                                            <span className="text-sm text-gray-600">
                                                                {additionalDocsFiles.length === 0 ? 'Add Document' : 'Add Another Document'}
                                                            </span>
                                                        </div>
                                                        <input
                                                            type="file"
                                                            accept=".pdf,.doc,.docx"
                                                            onChange={e => {
                                                                const file = e.target.files?.[0];
                                                                if (file && additionalDocsFiles.length < 10) {
                                                                    setAdditionalDocsFiles([...additionalDocsFiles, file]);
                                                                }
                                                                e.target.value = '';
                                                            }}
                                                            className="hidden"
                                                        />
                                                    </label>
                                                </div>
                                            )}

                                            {/* Upload button */}
                                            {additionalDocsFiles.length > 0 && (
                                                <button
                                                    onClick={async () => {
                                                        try {
                                                            setUploadingDocs(true);
                                                            const { data: { user } } = await supabase.auth.getUser();
                                                            if (!user) throw new Error('Not authenticated');

                                                            const uploadedUrls: string[] = [];

                                                            // Get existing URLs
                                                            if (profile.additional_docs_url) {
                                                                try {
                                                                    const parsed = JSON.parse(profile.additional_docs_url);
                                                                    if (Array.isArray(parsed)) {
                                                                        uploadedUrls.push(...parsed);
                                                                    } else {
                                                                        uploadedUrls.push(profile.additional_docs_url);
                                                                    }
                                                                } catch {
                                                                    uploadedUrls.push(profile.additional_docs_url);
                                                                }
                                                            }

                                                            // Upload new files
                                                            for (const file of additionalDocsFiles) {
                                                                const timestamp = Date.now();
                                                                const fileExt = file.name.split('.').pop();
                                                                const fileName = `${user.id}-${timestamp}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;

                                                                const { error: uploadError } = await supabase.storage
                                                                    .from('additional-docs')
                                                                    .upload(fileName, file, { cacheControl: '3600', upsert: true });

                                                                if (uploadError) {
                                                                    console.error('Upload error:', uploadError);
                                                                    continue;
                                                                }

                                                                const { data: urlData } = supabase.storage
                                                                    .from('additional-docs')
                                                                    .getPublicUrl(fileName);

                                                                uploadedUrls.push(urlData.publicUrl);
                                                            }

                                                            // Update database
                                                            const { error: updateError } = await supabase
                                                                .from('experts')
                                                                .update({
                                                                    additional_docs_url: uploadedUrls.length === 1
                                                                        ? uploadedUrls[0]
                                                                        : JSON.stringify(uploadedUrls)
                                                                })
                                                                .eq('id', profile.id);

                                                            if (updateError) throw updateError;

                                                            setProfile({ ...profile, additional_docs_url: JSON.stringify(uploadedUrls) });
                                                            setAdditionalDocsFiles([]);
                                                            setMessage('Additional documents uploaded successfully!');
                                                        } catch (error: any) {
                                                            setMessage('Error uploading documents: ' + error.message);
                                                        } finally {
                                                            setUploadingDocs(false);
                                                        }
                                                    }}
                                                    disabled={uploadingDocs}
                                                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                                                >
                                                    {uploadingDocs ? 'Uploading...' : `Upload ${additionalDocsFiles.length} Document${additionalDocsFiles.length > 1 ? 's' : ''}`}
                                                </button>
                                            )}

                                            <p className="text-xs text-gray-500 mt-2">
                                                PDF, DOC, or DOCX format. Maximum 10 documents total.
                                                {additionalDocsFiles.length > 0 && (
                                                    <span className="ml-1 font-medium">({additionalDocsFiles.length} selected)</span>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Account Settings - Make delete less prominent */}
                        <div className="border-t pt-8">
                            <h4 className="text-lg font-semibold text-gray-900 mb-4">Account Settings</h4>
                            <div className="space-y-6">
                                <div>
                                    <p className="text-sm text-gray-600 mb-2">Login Email</p>
                                    <p className="font-medium mb-3">{userEmail}</p>
                                    <button
                                        onClick={handlePasswordReset}
                                        disabled={loading}
                                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                                    >
                                        {loading ? 'Sending...' : 'Reset Password'}
                                    </button>
                                </div>

                                {/* Hide delete account in collapsible section */}
                                <details className="border-t pt-4">
                                    <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                                        Advanced Settings
                                    </summary>
                                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                                        <p className="text-sm text-gray-600 mb-3">
                                            Need to delete your account or have other account issues? Contact our support team.
                                        </p>
                                        <a
                                            href="mailto:support@expertlinkpro.com?subject=Account%20Support%20Request"
                                            className="inline-block px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm"
                                        >
                                            Contact Support
                                        </a>
                                        <p className="text-xs text-gray-500 mt-2">
                                            Email: support@expertlinkpro.com
                                        </p>
                                    </div>
                                </details>
                            </div>
                        </div>

                        {/* Subscription Management for Attorneys */}
                        {profile.type === 'attorney' && (
                            <div className="border-t pt-8">
                                <h4 className="text-lg font-semibold text-gray-900 mb-4">Subscription Management</h4>

                                {loadingSubscription ? (
                                    <div className="animate-pulse h-32 bg-gray-100 rounded"></div>
                                ) : subscription ? (
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <h5 className="font-medium text-green-900">Active Subscription</h5>
                                                <p className="text-sm text-green-700 mt-1">
                                                    ${subscription.amount ? (subscription.amount / 100).toFixed(2) : '0.00'}/month • {subscription.plan === 'unlimited' ? 'Unlimited expert searches & messaging' : 'Basic plan: 3 expert contacts & 1 case post per month'}
                                                </p>
                                                {subscription.current_period_end && (
                                                    <p className="text-sm text-green-600 mt-2">
                                                        Next billing date: {new Date(subscription.current_period_end).toLocaleDateString()}
                                                    </p>
                                                )}
                                                {subscription.stripe_subscription_id?.startsWith('test_') && (
                                                    <span className="inline-block mt-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                                                        Test Subscription
                                                    </span>
                                                )}
                                            </div>
                                            <button
                                                onClick={cancelSubscription}
                                                disabled={cancellingSubscription}
                                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400"
                                            >
                                                {cancellingSubscription ? 'Cancelling...' : 'Cancel Subscription'}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                                        <h5 className="font-medium text-gray-900">No Active Subscription</h5>
                                        <p className="text-sm text-gray-600 mt-1">
                                            Subscribe to unlock unlimited expert searches and messaging.
                                        </p>
                                        <button
                                            onClick={() => setShowSubscriptionModal(true)}
                                            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                        >
                                            Subscribe Now
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {showSubscriptionModal && (
                            <SubscriptionModal
                                onSuccess={() => {
                                    setShowSubscriptionModal(false);
                                    loadSubscription();
                                    setMessage('Subscription activated successfully!');
                                }}
                                onCancel={() => setShowSubscriptionModal(false)}
                            />
                        )}

                        {/* Messages */}
                        {message && (
                            <div className={`p-4 rounded-lg ${
                                message.includes('Error') || message.includes('cancelled')
                                    ? 'bg-red-50 border border-red-200 text-red-700'
                                    : 'bg-green-50 border border-green-200 text-green-700'
                            }`}>
                                {message}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}